import { useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { OrgNav } from '../components/OrgNav.js';
import { Card, CopyLink, ErrorBanner, Loading, Spinner } from '../components/ui.js';
import {
  api,
  type Invitation,
  type JoinCode,
  type Member,
  type Organization,
  type Role,
} from '../lib/api.js';
import { formatDate, formatRelative, titleCase } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/** Members and invitations. */
export function MembersPage(): ReactNode {
  const { orgId = '' } = useParams();

  const org = useAsync(() => api.get<Organization>(`/api/organizations/${orgId}`), [orgId]);
  const members = useAsync(
    () => api.get<{ data: Member[] }>(`/api/organizations/${orgId}/members`),
    [orgId],
  );

  const canManage = org.data?.role === 'owner' || org.data?.role === 'admin';
  const isOwner = org.data?.role === 'owner';

  const invitations = useAsync(
    () =>
      canManage
        ? api.get<{ data: Invitation[] }>(`/api/organizations/${orgId}/invitations`)
        : Promise.resolve({ data: [] as Invitation[] }),
    [orgId, canManage],
  );

  const { run, pending, error } = useAction();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [lastInvite, setLastInvite] = useState<Invitation | null>(null);
  const [showLinkFor, setShowLinkFor] = useState<string | null>(null);

  const linkShown =
    (invitations.data?.data ?? []).find((invite) => invite.id === showLinkFor) ?? null;

  if (org.loading || members.loading) return <Loading />;

  async function invite(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      const created = await api.post<Invitation>(`/api/organizations/${orgId}/invitations`, {
        email,
        role,
      });
      setEmail('');
      setLastInvite(created);
      invitations.reload();
    });
  }

  async function changeRole(memberId: string, nextRole: Role): Promise<void> {
    await run(async () => {
      await api.patch(`/api/organizations/${orgId}/members/${memberId}`, { role: nextRole });
      members.reload();
    });
  }

  async function remove(memberId: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/organizations/${orgId}/members/${memberId}`);
      members.reload();
    });
  }

  async function cancelInvite(invitationId: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/organizations/${orgId}/invitations/${invitationId}`);
      invitations.reload();
    });
  }

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>Students</h1>
          <p>Who can join your classes, and the codes that let them in.</p>
        </div>
      </div>

      <OrgNav orgId={orgId} />

      <div className="stack">
        <ErrorBanner error={members.error} />
        <ErrorBanner message={error} />

        <Card title="People">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {(members.data?.data ?? []).map((member) => (
                <tr key={member.id}>
                  <td>{member.user.name}</td>
                  <td className="muted">{member.user.email}</td>
                  <td>
                    {canManage ? (
                      <select
                        value={member.role}
                        disabled={pending}
                        onChange={(e) => void changeRole(member.id, e.target.value as Role)}
                        style={{ width: 'auto' }}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        {/* Only an owner may grant ownership; the server enforces this too. */}
                        <option value="owner" disabled={!isOwner}>
                          Owner
                        </option>
                      </select>
                    ) : (
                      <span className="badge">{titleCase(member.role)}</span>
                    )}
                  </td>
                  <td className="muted small">{formatDate(member.createdAt)}</td>
                  {canManage && (
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="button-danger"
                        disabled={pending}
                        onClick={() => void remove(member.id)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {canManage && (
            <p className="field-hint" style={{ marginTop: '0.75rem' }}>
              An organization always keeps at least one owner — the last one cannot be removed or
              demoted.
            </p>
          )}
        </Card>

        {canManage && (
          <Card title="Invite someone">
            <form onSubmit={(e) => void invite(e)} className="stack">
              <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="invite-email">Email</label>
                  <input
                    id="invite-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="colleague@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="invite-role">Role</label>
                  <select
                    id="invite-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as Role)}
                    style={{ width: 'auto' }}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="owner" disabled={!isOwner}>
                      Owner
                    </option>
                  </select>
                </div>
                <button type="submit" className="button-primary" disabled={pending}>
                  {pending && <Spinner />}
                  Invite
                </button>
              </div>
            </form>

            {/*
              Invitation email delivery is not configured on the server, so the
              link is surfaced here. Without this the invitation would be
              unusable. The URL comes from the server rather than from
              `window.location`, so it is the same one the server logged.
            */}
            {lastInvite && (
              <div className="banner banner-success" style={{ marginTop: '1rem' }}>
                <div>
                  Invitation created for {lastInvite.email}. Email delivery is not configured, so
                  send them this link — it expires {formatRelative(lastInvite.expiresAt)}.
                </div>
                <div style={{ marginTop: '0.4rem' }}>
                  <CopyLink value={lastInvite.acceptUrl} label="Copy link" />
                </div>
              </div>
            )}
          </Card>
        )}

        {canManage && (invitations.data?.data.length ?? 0) > 0 && (
          <Card title="Pending invitations">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(invitations.data?.data ?? []).map((invitation) => (
                  <tr key={invitation.id}>
                    <td>{invitation.email}</td>
                    <td>
                      <span className="badge">{titleCase(invitation.role)}</span>
                    </td>
                    <td className="muted small">{titleCase(invitation.status)}</td>
                    <td className="muted small">{formatRelative(invitation.expiresAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {invitation.status === 'pending' && (
                        <div className="button-row" style={{ justifyContent: 'flex-end' }}>
                          {/*
                            Per row, not just for the invitation created in this
                            session: without delivery, someone who reloads the
                            page would otherwise have no way to retrieve a link
                            they had not yet passed on.
                          */}
                          <button
                            type="button"
                            className="button-ghost"
                            onClick={() =>
                              setShowLinkFor(showLinkFor === invitation.id ? null : invitation.id)
                            }
                          >
                            {showLinkFor === invitation.id ? 'Hide link' : 'Show link'}
                          </button>
                          <button
                            type="button"
                            className="button-danger"
                            disabled={pending}
                            onClick={() => void cancelInvite(invitation.id)}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {linkShown && (
              <div style={{ marginTop: '0.75rem' }}>
                <p className="field-hint">Accept link for {linkShown.email}</p>
                <CopyLink value={linkShown.acceptUrl} label="Copy link" />
              </div>
            )}
          </Card>
        )}

        {canManage && <JoinCodes orgId={orgId} />}
      </div>
    </div>
  );
}





/**
 * Join codes: how a learner subscribes without the publisher knowing their
 * email.
 *
 * Placed on this page rather than its own because "who is in" and "how do they
 * get in" are one question. Invitations and codes are two answers to it, and
 * seeing them together is what stops a publisher inviting forty people
 * individually when a code would have done.
 */
function JoinCodes({ orgId }: { orgId: string }): ReactNode {
  const codes = useAsync(
    () => api.get<{ data: JoinCode[] }>(`/api/organizations/${orgId}/join-codes`),
    [orgId],
  );
  const { run, pending, error } = useAction();
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState('');

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.post(`/api/organizations/${orgId}/join-codes`, {
        label,
        maxUses: maxUses ? Number(maxUses) : null,
      });
      setLabel('');
      setMaxUses('');
      codes.reload();
    });
  }

  async function revoke(id: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/organizations/${orgId}/join-codes/${id}`);
      codes.reload();
    });
  }

  const list = codes.data?.data ?? [];

  return (
    <Card title="Join codes">
      <ErrorBanner error={codes.error} />
      <ErrorBanner message={error} />

      <p className="field-hint">
        Share a code and learners subscribe themselves — no email address needed. Read it out in
        class, put it on the board, or paste it into a group chat. Anyone with the code can join
        until you revoke it or it reaches its limit.
      </p>

      {codes.loading ? (
        <Loading />
      ) : list.length === 0 ? (
        <p className="muted small">No codes yet.</p>
      ) : (
        <table style={{ marginTop: '0.75rem' }}>
          <thead>
            <tr>
              <th>Code</th>
              <th>For</th>
              <th>Student</th>
              <th>Used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((code) => (
              <tr key={code.id}>
                <td>
                  {/*
                    Large and monospaced because this gets read aloud and copied
                    by hand. The alphabet already excludes look-alike characters;
                    a proportional font would undo half of that benefit.
                  */}
                  <span className="mono" style={{ fontSize: '1.15rem', letterSpacing: '0.08em' }}>
                    {code.code}
                  </span>
                  {code.status !== 'active' && (
                    <span className="badge" style={{ marginLeft: '0.5rem' }}>
                      {code.status}
                    </span>
                  )}
                </td>
                <td className="small">{code.label}</td>
                <td className="small">
                  {code.issuedToEmail === null ? (
                    code.redeemedByEmail === null ? (
                      /* Handed out by the publisher; nobody has used it yet. */
                      <span className="muted">Unclaimed</span>
                    ) : (
                      /*
                       * A batch code, now claimed. This is the only record of who
                       * used it — nobody told us who it was given to.
                       */
                      <>
                        <div>{code.redeemedByEmail}</div>
                        <div className="muted small">claimed</div>
                      </>
                    )
                  ) : (
                    <>
                      <div>{code.issuedToEmail}</div>
                      {/*
                        Three states worth distinguishing, because they need
                        different actions: joined (nothing to do), emailed but not
                        joined (chase them), and not emailed (the send failed —
                        pass the code on by hand).
                      */}
                      <div className="muted small">
                        {code.redeemedAt !== null
                          ? /*
                             * Worth flagging rather than just saying "joined":
                             * a code issued to one address and used by another
                             * was forwarded, and the publisher is the only one
                             * who can decide whether that matters.
                             */
                            code.redeemedByEmail !== null &&
                            code.redeemedByEmail !== code.issuedToEmail
                            ? `joined as ${code.redeemedByEmail}`
                            : 'joined'
                          : code.sentAt !== null
                            ? 'emailed, not joined yet'
                            : 'not emailed'}
                      </div>
                    </>
                  )}
                </td>
                <td className="small mono">
                  {code.usedCount}
                  {code.maxUses === null ? '' : ` / ${String(code.maxUses)}`}
                </td>
                <td>
                  <div className="button-row">
                    <CopyLink value={code.code} label="Copy code" />
                    {/*
                      A link as well as the code: pasted into a chat it is one
                      tap, where a bare code needs the learner to find the field
                      to type it into.
                    */}
                    <CopyLink
                      value={`${window.location.origin}/join/${code.code}`}
                      label="Copy link"
                    />
                    {code.status === 'active' && (
                      <button
                        type="button"
                        className="button-danger"
                        disabled={pending}
                        onClick={() => void revoke(code.id)}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={(e) => void create(e)} className="stack" style={{ marginTop: '1rem' }}>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label htmlFor="jc-label">What is this code for?</label>
            <input
              id="jc-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Class 10 batch A"
              required
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="jc-max">Limit</label>
            <input
              id="jc-max"
              type="number"
              min="1"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="no limit"
            />
            <p className="field-hint">
              Set it to the size of the batch. A code shared in a chat can travel further than you
              expect, and a limit is what bounds that.
            </p>
          </div>
          <button type="submit" className="button-primary" disabled={pending}>
            {pending && <Spinner />}
            Create code
          </button>
        </div>
      </form>

      <p className="small faint" style={{ marginTop: '0.75rem' }}>
        Revoking a code stops new learners joining. It does not remove anyone who already joined —
        remove them from the list above if you need to.
      </p>

    </Card>
  );
}


